import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { borderRadius } from 'styled-system';
import { useTranslation } from 'react-i18next';

import { withMixpanel } from 'Context/AnalyticsContext/hoc';
import ContentUserAsset from 'Domain/Classroom/ContentUserAsset';
import { useClassroomNavigation } from 'Util/Hooks';
import { useClassroomTracking } from 'Util/Analytics';

import { Text, Flex } from '../Base';

import Content, { MATERIAL_TYPE, SUBSECTION_TYPE, CONTENT_DATA } from './Content';
const CONTENT_PRIORITY = [
  MATERIAL_TYPE.VIDEO_SLIDE,
  MATERIAL_TYPE.VIDEO,
  MATERIAL_TYPE.LIVE_VIDEO,
  MATERIAL_TYPE.YOUTUBE,
  MATERIAL_TYPE.NOTEBOARD,
  MATERIAL_TYPE.ARTICLE,
  MATERIAL_TYPE.ASSIGNMENT,
  MATERIAL_TYPE.EXTERNAL,
  MATERIAL_TYPE.EBOOK,
  MATERIAL_TYPE.PDF,
  MATERIAL_TYPE.OTHER,
];

const CONTENT_MARK_COMPLETED_PRIORITY = [
  MATERIAL_TYPE.QUIZ,
  MATERIAL_TYPE.QUIZ_SIMPLE,
  MATERIAL_TYPE.LIVECODING,
  MATERIAL_TYPE.VIDEO_SLIDE,
  MATERIAL_TYPE.VIDEO,
  MATERIAL_TYPE.LIVE_VIDEO,
  MATERIAL_TYPE.YOUTUBE,
  MATERIAL_TYPE.NOTEBOARD,
  MATERIAL_TYPE.ARTICLE,
  MATERIAL_TYPE.ASSIGNMENT,
  MATERIAL_TYPE.EBOOK,
  MATERIAL_TYPE.PDF,
  MATERIAL_TYPE.MATERIAL,
];

const mapContent = ({ content, title, ...restProps }) => ({
  ...restProps,
  contentTitle: title,
  value: content,
});

const sort = (array = [], contentPriority = CONTENT_PRIORITY, materials = []) => {
  let result = [];
  contentPriority.forEach((priorityType) => {
    if (priorityType === MATERIAL_TYPE.MATERIAL && Array.isArray(materials) && materials.length) {
      result.push({
        content: materials,
        type: MATERIAL_TYPE.MATERIAL,
        id: 'downloadable-materials',
      });
    } else if (priorityType === MATERIAL_TYPE.EBOOK) {
      const item = array.find((item) => item.type === MATERIAL_TYPE.PDF);
      if (item) {
        result.push({ ...item, type: MATERIAL_TYPE.EBOOK });
      }
    } else {
      result = [...result, ...array.filter((item) => item.type === priorityType)];
    }
  });
  return result;
};

const getContentCompletePriorityIndex = (
  contents = [],
  markCompletePriority = CONTENT_MARK_COMPLETED_PRIORITY
) => {
  let markCompleteIndex = 0;
  if (!Array.isArray(contents) || !contents.length) return markCompleteIndex;

  markCompletePriority.some((priorityType) => {
    markCompleteIndex = contents.findIndex(({ type: contentType }) => contentType === priorityType);
    return markCompleteIndex > -1;
  });

  return markCompleteIndex > -1 ? markCompleteIndex : 0;
};

const LiveCodeLayout = styled(Flex)`
  ${borderRadius}
`;

const ContentWrapper = withMixpanel(
  ({
    title,
    revision,
    description,
    type,
    items,
    materials,
    secureContent,
    onCompleted,
    videoOverlayDisplayName,
    videoOverlayPhoneNumber,
    onVideoOverlayCorrupted,
    onUpdateSubSectionData,
    subSectionStatus,
    isResuming,
    checkInvalidDevice,
    onUpdateClassroomTime,
    allowedTypes,
    isAppStateInactive,
    onVideoError,
    videoOrigin,
    delaySetCurrentTime,
    userProfileData,
    onDownloadClick,
    downloadUrl,
    onUpdateQuizSimpleResult,
    courseId,
    courseTitle,
    courseProgress,
    getDownloadlink,
    onExitClassroom,
    userProfileData: { userId },
    mixpanel,
    classroomCourseId,
    currentSubsectionReferId,
    isLearnCraftNewLayout,
  }) => {
    const { t } = useTranslation('classroom');
    const { isLastSubsectionInCourse } = useClassroomNavigation();
    const { logSubsectionVideoOnVideoPlayerError } = useClassroomTracking();

    useEffect(() => {
      if (mixpanel) {
        mixpanel.identify(userId);
        mixpanel.track('Study in Classroom', {
          'Course Name': courseTitle,
          'Subsection Name': title,
          Type: type,
          'Current Course Progress': courseProgress,
        });
      }
    }, []);

    if (allowedTypes && allowedTypes.length > 0 && !allowedTypes.includes(type)) {
      return (
        <Text px={5} py={3}>
          {t('CONTENT.NO_CONTENT')}
        </Text>
      );
    } else {
      const contents = sort(items, allowedTypes, materials);
      const markCompleteIndex = getContentCompletePriorityIndex(contents);

      //  FIXME: refactor handling article content is included on liveCoding subsection
      const isRowLayout = type === SUBSECTION_TYPE.LIVECODING && contents.length === 2;
      const LayoutWrapper = isRowLayout ? LiveCodeLayout : Fragment;
      const layoutWrapperProps = isRowLayout
        ? {
            flexDirection: ['column', null, null, 'row'],
            mx: '36px',
            backgroundColor: 'white',
            borderRadius: '12px',
          }
        : {};

      return (
        <LayoutWrapper {...layoutWrapperProps}>
          {contents.map((contentItem, index) => {
            const { referId: assetReferId } = contentItem;
            // TODO: this width should depend on open/close status of ToC
            return (
              <Fragment key={contentItem.id}>
                <ContentUserAsset assetReferId={assetReferId}>
                  {({
                    userAssetDocument,
                    isUserAssetDataLoading,
                    userAssetDataError,
                    handleUpdateUserAsset,
                  }) => (
                    <Content
                      title={title}
                      courseId={courseId}
                      revision={revision}
                      key={contentItem.id}
                      classroomCourseId={classroomCourseId}
                      subsectionType={type}
                      onCompleted={(
                        videoWatchedDuration,
                        videoCompletionThreshold,
                        videoTotalDuration,
                        videoProvider
                      ) =>
                        markCompleteIndex === index
                          ? onCompleted(
                              videoWatchedDuration,
                              videoCompletionThreshold,
                              videoTotalDuration,
                              videoProvider
                            )
                          : undefined
                      }
                      {...mapContent(contentItem)}
                      getDownloadlink={getDownloadlink}
                      secureContent={secureContent}
                      videoOverlayDisplayName={videoOverlayDisplayName}
                      videoOverlayPhoneNumber={videoOverlayPhoneNumber}
                      onVideoOverlayCorrupted={onVideoOverlayCorrupted}
                      subSectionStatus={subSectionStatus}
                      onUpdateSubSectionData={onUpdateSubSectionData}
                      isResuming={isResuming}
                      checkInvalidDevice={checkInvalidDevice}
                      onUpdateClassroomTime={onUpdateClassroomTime}
                      isAppStateInactive={isAppStateInactive}
                      onVideoError={onVideoError}
                      videoOrigin={videoOrigin}
                      delaySetCurrentTime={delaySetCurrentTime}
                      userProfileData={userProfileData}
                      onDownloadClick={onDownloadClick}
                      downloadUrl={downloadUrl}
                      onUpdateQuizSimpleResult={onUpdateQuizSimpleResult}
                      liveCodingProps={{ hasArticle: isRowLayout }}
                      userAssetDocument={userAssetDocument}
                      userAssetDataError={userAssetDataError}
                      isUserAssetDataLoading={isUserAssetDataLoading}
                      isLastSubsectionInCourse={isLastSubsectionInCourse}
                      onUpdateUserAsset={handleUpdateUserAsset}
                      onExitClassroom={onExitClassroom}
                      currentSubsectionReferId={currentSubsectionReferId}
                      isLearnCraftNewLayout={isLearnCraftNewLayout}
                      logSubsectionVideoOnVideoPlayerError={logSubsectionVideoOnVideoPlayerError}
                    />
                  )}
                </ContentUserAsset>
              </Fragment>
            );
          })}
        </LayoutWrapper>
      );
    }
  }
);

ContentWrapper.propTypes = {
  items: PropTypes.array,
  materials: PropTypes.array,
  type: PropTypes.oneOf(Object.values(MATERIAL_TYPE)),
  secureContent: PropTypes.shape({
    video: PropTypes.func,
    live: PropTypes.func,
    assets: PropTypes.func,
  }),
  subSectionStatus: PropTypes.shape({
    videoProgress: PropTypes.object,
  }),
  isResuming: PropTypes.bool,
  checkInvalidDevice: PropTypes.func,
  onUpdateClassroomTime: PropTypes.func,
  allowedTypes: PropTypes.array,
  isAppStateInactive: PropTypes.bool,
  onVideoError: PropTypes.func,
  videoOrigin: PropTypes.string,
  delaySetCurrentTime: PropTypes.number,
  userProfileData: PropTypes.object,
  onDownloadClick: PropTypes.func,
  downloadUrl: PropTypes.string,
  mixpanel: PropTypes.object,
  courseId: PropTypes.string,
  courseTitle: PropTypes.string,
  title: PropTypes.string,
  revision: PropTypes.string,
  courseProgress: PropTypes.number,
  onExitClassroom: PropTypes.func,
  isLearnCraftNewLayout: PropTypes.bool,
};

ContentWrapper.defaultProps = {
  items: [],
  materials: [],
  type: MATERIAL_TYPE.VIDEO,
  secureContent: {
    video: () => {},
    live: () => {},
    assets: () => {},
  },
  subSectionStatus: {},
  isResuming: false,
  checkInvalidDevice: () => {},
  onUpdateClassroomTime: () => {},
  onVideoError: () => {},
  onDownloadClick: () => {},
  onExitClassroom: () => {},
  isLearnCraftNewLayout: false,
};

export { CONTENT_DATA, ContentWrapper };
export default ContentWrapper;
